import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Loads recently viewed products from localStorage via the Search + Section Rendering API.
 */
class RecentlyViewedProducts extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  connectedCallback() {
    if (this.dataset.loaded === 'true') return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
  }

  async #load() {
    if (this.dataset.loaded === 'true') return;

    const currentProductId = this.dataset.productId || '';
    const maxProducts = Number(this.dataset.maxProducts || 4);
    const sectionId = this.dataset.sectionId;
    const id = this.id;

    if (!sectionId || !id) {
      this.hidden = true;
      return;
    }

    const productIds = RecentlyViewed.getProducts()
      .filter(/** @param {string} productId */ (productId) => productId !== currentProductId)
      .slice(0, maxProducts);

    if (productIds.length === 0) {
      this.hidden = true;
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map(/** @param {string} productId */ (productId) => `id:${productId}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector(`recently-viewed-products[id="${CSS.escape(id)}"]`);

      if (!next?.innerHTML?.trim()) {
        this.hidden = true;
        return;
      }

      const hasProducts = next.querySelector('[data-has-products="true"]');
      if (!hasProducts) {
        this.hidden = true;
        return;
      }

      this.innerHTML = next.innerHTML;
      this.dataset.loaded = 'true';
    } catch (error) {
      console.error('Recently viewed products error:', error);
      this.hidden = true;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
